<script lang="ts" setup>
import {ref} from 'vue'
import Breadcrumb from '@/components/Breadcrumb.vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import type {SelectOption} from "~/types";
import type {EventSearch} from "~/types/event";

const {show, hide} = useLoading()
const route = useRoute()
const props = defineProps({
  breadcrumbData: {type: Array, required: true},
  categorySlug: {type: String, default: ''}
})

const {findAllEventsBySearch} = useEvent()
const {findAllCategories} = useCategory()
show()

const {data: categories} = await useAsyncData<SelectOption[]>(`categories-list-options`, async () => {
      const allCategories = await findAllCategories();
      return allCategories.map(({slug, description}) => ({
        label: description,
        value: slug
      }))
    },
    {default: () => []}
);

const {filterType, filterCity, geolocation} = useNavSearch()
const events = ref([]);
const hasMoreElements = ref(false)
const filters = reactive<EventSearch>({
  query: route.query?.q || null,
  city: filterCity.value,
  categorySlug: null,
  subCategorySlug: null,
  startDate: null,
  latitude: geolocation.value.latitude,
  longitude: geolocation.value.longitude,
  distanceAreaMeters: 50000,
  sortBy: 'rank',
  page: 0,
  size: 20
});

const relevantFilters = computed(() => {
  const {query, city, categorySlug, subCategorySlug, startDate, latitude, longitude, distanceAreaMeters} = filters;
  return {query, city, categorySlug, subCategorySlug, startDate, latitude, longitude, distanceAreaMeters};
});

const title = computed(() => {
  const bd = props.breadcrumbData[0]
  return bd.subitems[bd.subitems.length - 1].title
})

const fetchEvents = async () => {
  // if (route?.query?.q) filters.query = route.query.q
  if (props.categorySlug) filters.categorySlug = props.categorySlug
  const data = await findAllEventsBySearch(filters);
  for (const evt of data.content) {
    if (!events.value.find(({id}) => id === evt.id))
      events.value.push(evt)
  }
  hasMoreElements.value = !data.last
  hide()
};

watch(geolocation, (g) => {
  filters.latitude = g.latitude;
  filters.longitude = g.longitude;
})

watch(filterCity, (val) => filters.city = val);

watch(relevantFilters, () => {
  filters.page = 0
  events.value = []
}, {deep: true});

onMounted(fetchEvents);
watch(filterType, fetchEvents)
watch(filters, fetchEvents);

</script>
<template>
  <div class="container mb-md-4 pb-5">
    <Breadcrumb :data="breadcrumbData"/>
    <div class="row">
      <div class="col-12">
        <div class="d-flex flex-column flex-sm-row align-items-center justify-content-between mb-4">
          <h1 class="h2 text-center text-sm-start mb-4">{{ title }}</h1>
          <div class="d-sm-flex align-items-center flex-wrap ms-sm-4">
            <!-- Filtro por categoria -->
            <div class="d-flex align-items-center mb-3 me-sm-4 w-100 w-sm-auto">
              <label class="fs-sm me-2 pe-1 text-nowrap" for="category-filter">
                <i class="fi-filter text-muted mt-n1 me-2"></i>
                Filtrar Por:
              </label>
              <div class="btn-group dropdown">
                <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown"
                        aria-haspopup="true" aria-expanded="false">
                  Categorias
                </button>
                <div class="dropdown-menu my-1">
                  <NuxtLink v-for="(item, i) in categories" :key="i" :value="item.value" class="dropdown-item"
                            :to="`/eventos/categorias/${item.value}`"> {{ item.label }}
                  </NuxtLink>
                </div>
              </div>
            </div>

            <!-- Filtro por data -->
            <div class="input-group mb-3 w-100 w-sm-auto me-2">
              <VueDatePicker
                  locale="pt-br"
                  :range="true"
                  v-model="filters.startDate"
                  :enable-time-picker="false"
              ></VueDatePicker>
            </div>

            <!-- Ordenar por -->
            <div class="d-flex align-items-center w-100 w-sm-auto mb-3">
              <label class="fs-sm me-2 pe-1 text-nowrap" for="sortby">
                <i class="fi-arrows-sort text-muted mt-n1 me-2"></i>
                Ordenar Por:
              </label>
              <select class="form-select form-select-sm w-100 w-sm-auto" id="sortby" v-model="filters.sortBy">
                <option value="rank">Relevância</option>
                <option value="name">Nome</option>
                <option value="startAt">Data</option>
                <option value="distance" v-if="filters.latitude > 0">Distância</option>
                <option value="createdAt">Mais Novo</option>
              </select>
            </div>
          </div>
        </div>

        <div class="row g-4 g-md-3 g-lg-4 pt-2">
          <div v-if="events.length === 0" class="w-100 text-center">
            <h5>Sem resultados<br>Não encontramos eventos que corresponda à sua busca.<br>Tente buscar outras
              palavras-chave</h5>
          </div>
          <div v-for="(property, index) in events" :key="index" class="col-sm-6 col-xl-3">
            <EventCard :event="property"/>
          </div>

          <div v-if="hasMoreElements" class="text-center mt-1 pt-4">
            <a class="d-inline-block fw-bold text-decoration-none p-2 pb-0" href="javascript:void(0);"
               @click="filters.page++">
              <i class="fi-refresh fs-sm me-2"></i>
              Carregar mais
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
